const HEIC_EXTENSION = /\.(heic|heif)$/i;
const HEIC_URL = /\.(heic|heif)(\?|#|$)/i;

// A modern iPhone HEIC is ~24MP and very efficiently encoded (a few MB). Decoding
// it and re-encoding to JPEG at full resolution balloons it well past upload
// limits — a 6 MB HEIC can become a >10 MB JPEG. Cap the longest edge and encode
// at a sane quality so the converted photo stays sharp but lands comfortably
// under the limit.
const MAX_EDGE = 2560;
const JPEG_QUALITY = 0.8;

export const isHeicFile = (file: File): boolean => {
  const type = file.type.toLowerCase();
  return type === 'image/heic' || type === 'image/heif' || HEIC_EXTENSION.test(file.name);
};

export const isHeicUrl = (url: string): boolean => HEIC_URL.test(url);

// Downscale (if needed) and re-encode a JPEG blob so the payload stays small.
// Falls back to the original blob if the canvas pipeline is unavailable.
const compressJpegBlob = async (blob: Blob): Promise<Blob> => {
  try {
    const bitmap = await createImageBitmap(blob);
    try {
      const longest = Math.max(bitmap.width, bitmap.height);
      const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return blob;
      ctx.drawImage(bitmap, 0, 0, width, height);
      const out = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
      );
      return out ?? blob;
    } finally {
      bitmap.close();
    }
  } catch {
    return blob;
  }
};

const toJpegBlob = async (blob: Blob): Promise<Blob> => {
  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
  const jpeg = Array.isArray(converted) ? converted[0] : converted;
  return compressJpegBlob(jpeg);
};

export const convertHeicToJpegFile = async (file: File): Promise<File> => {
  if (!isHeicFile(file)) return file;
  try {
    const jpeg = await toJpegBlob(file);
    const baseName = file.name.replace(HEIC_EXTENSION, '');
    return new File([jpeg], `${baseName || 'image'}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
};

export const convertHeicUrlToObjectUrl = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const jpeg = await toJpegBlob(await response.blob());
    return URL.createObjectURL(jpeg);
  } catch {
    return null;
  }
};
